import api from './api';

// Extend Window interface for Razorpay
declare global {
  interface Window {
    Razorpay: any;
  }
}

export interface SubscriptionPlan {
  id: string;
  name: string;
  description: string;
  amount: number;
  currency: string;
  period: string;
  savings?: string;
  features: string[];
}

export interface TestLimits {
  used: number;
  limit: number;
  remaining: string | number;
  canTake: boolean;
}

export interface SubscriptionStatus {
  plan: 'free' | 'monthly' | 'yearly';
  status: string;
  isActive: boolean;
  startDate?: string;
  endDate?: string;
  testLimits: {
    math: TestLimits;
    science: TestLimits;
    english: TestLimits;
    gk: TestLimits;
  };
}

export interface PaymentOrder {
  id: string;
  amount: number;
  currency: string;
  key: string;
}

export interface PaymentHistory {
  id: string;
  orderId: string;
  amount: number;
  status: string;
  date: string;
  formattedAmount: string;
}

class SubscriptionService {
  // Get available subscription plans
  async getPlans(): Promise<SubscriptionPlan[]> {
    try {
      const response = await api.get('/payments/plans');
      return response.data.plans;
    } catch (error) {
      console.error('Error fetching subscription plans:', error);
      throw error;
    }
  }

  // Get user's subscription status
  async getSubscriptionStatus(): Promise<SubscriptionStatus> {
    try {
      const response = await api.get('/payments/subscription/status');
      return response.data.subscription;
    } catch (error) {
      console.error('Error fetching subscription status:', error);
      throw error;
    }
  }

  // Create payment order
  async createPaymentOrder(planType: string): Promise<{
    order: PaymentOrder;
    plan: any;
    student: any;
  }> {
    try {
      const response = await api.post('/payments/create-order', { planType });
      return response.data;
    } catch (error) {
      console.error('Error creating payment order:', error);
      throw error;
    }
  }

  // Verify payment and activate subscription
  async verifyPayment(paymentData: {
    razorpay_order_id: string;
    razorpay_payment_id: string;
    razorpay_signature: string;
    planType: string;
  }): Promise<any> {
    try {
      const response = await api.post('/payments/verify-payment', paymentData);
      return response.data;
    } catch (error) {
      console.error('Error verifying payment:', error);
      throw error;
    }
  }

  // Check if user can take a test in a subject
  async checkTestAccess(subject: string): Promise<{
    canTakeTest: boolean;
    remainingTests: string | number;
    plan: string;
    isActive: boolean;
    needsUpgrade: boolean;
  }> {
    try {
      const response = await api.post('/payments/check-test-access', { subject });
      return response.data;
    } catch (error) {
      console.error('Error checking test access:', error);
      throw error;
    }
  }

  // Get payment history
  async getPaymentHistory(): Promise<PaymentHistory[]> {
    try {
      const response = await api.get('/payments/subscription/history');
      return response.data.payments;
    } catch (error) {
      console.error('Error fetching payment history:', error);
      throw error;
    }
  }

  // Load Razorpay script
  loadRazorpayScript(): Promise<boolean> {
    return new Promise((resolve) => {
      // Check if already loaded
      if (window.Razorpay) {
        resolve(true);
        return;
      }

      const script = document.createElement('script');
      script.src = 'https://checkout.razorpay.com/v1/checkout.js';
      script.onload = () => resolve(true);
      script.onerror = () => resolve(false);
      document.body.appendChild(script);
    });
  }

  // Process payment using Razorpay
  async processPayment(planType: string): Promise<boolean> {
    try {
      // Load Razorpay script
      const scriptLoaded = await this.loadRazorpayScript();
      if (!scriptLoaded) {
        throw new Error('Failed to load Razorpay SDK');
      }

      // Create payment order
      const { order, plan, student } = await this.createPaymentOrder(planType);

      return new Promise((resolve, reject) => {
        const options = {
          key: order.key,
          amount: order.amount,
          currency: order.currency,
          name: 'Apex Scholar League',
          description: plan.description,
          order_id: order.id,
          prefill: {
            name: student.name,
            email: student.email,
            contact: student.phone,
          },
          theme: {
            color: '#33AD69', // Using user's theme color from memory
          },
          modal: {
            ondismiss: () => {
              reject(new Error('Payment cancelled by user'));
            },
          },
          handler: async (response: any) => {
            try {
              // Verify payment on backend
              const verificationResult = await this.verifyPayment({
                razorpay_order_id: response.razorpay_order_id,
                razorpay_payment_id: response.razorpay_payment_id,
                razorpay_signature: response.razorpay_signature,
                planType,
              });

              if (verificationResult.success) {
                resolve(true);
              } else {
                reject(new Error('Payment verification failed'));
              }
            } catch (error) {
              reject(error);
            }
          },
        };

        const razorpay = new (window as any).Razorpay(options);
        razorpay.open();
      });
    } catch (error) {
      console.error('Error processing payment:', error);
      throw error;
    }
  }

  // Format price for display
  formatPrice(amount: number): string {
    return `₹${(amount / 100).toFixed(0)}`;
  }

  // Get savings text for yearly plan
  getSavingsText(): string {
    const monthlyAnnual = 200 * 12; // ₹200 × 12 months
    const yearlyPrice = 2000;
    const savings = monthlyAnnual - yearlyPrice;
    return `Save ₹${savings}!`;
  }

  // Get plan badge color
  getPlanBadgeColor(plan: string): string {
    switch (plan) {
      case 'free':
        return '#9ca3af'; // gray
      case 'monthly':
        return '#3b82f6'; // blue
      case 'yearly':
        return '#10b981'; // emerald
      default:
        return '#9ca3af';
    }
  }

  // Get plan display name
  getPlanDisplayName(plan: string): string {
    switch (plan) {
      case 'free':
        return 'Free Plan';
      case 'monthly':
        return 'Monthly Plan';
      case 'yearly':
        return 'Yearly Plan';
      default:
        return 'Unknown Plan';
    }
  }
}

export default new SubscriptionService(); 